import { useState } from 'react';

import Button from 'components/ui/button';
import { UserAvatar } from 'components/user-avatar';

import CamadaDecorativa, { IconeDecorativo } from 'components/camada-decorativa';

import logoJogaJunto from 'assets/img/logo-jj.png';
import IconePromocao from 'assets/img/decorativos/promocao.svg';
import IconeTimeEquipe from 'assets/img/decorativos/time-equipe.svg';

import { baixarArquivo, DadosDoCartao, gerarCartaoPng, nomeDoArquivoDoCartao } from '../cartao-do-ciclo';
import { PessoaDoPerfil } from './PerfilHero';
import { TITULO_DE_SECAO } from 'styles';

// Posições e tamanhos do Meu Perfil.dc.html (IconPromocao e IconTimeEquipe).
const DECORACAO: IconeDecorativo[] = [
  { src: IconePromocao, tamanho: 80, estilo: { right: '-8%', bottom: '-14%', transform: 'rotate(14deg)' } },
  { src: IconeTimeEquipe, tamanho: 64, estilo: { left: '-7%', bottom: '-10%', transform: 'rotate(-12deg)' } },
];

interface CartaoCompartilharProps {
  dados: DadosDoCartao;
  avatar: PessoaDoPerfil['avatar'];
  /** URL da foto quando a pessoa usa imagem; sem foto, o PNG desenha o avatar montado (PERFIL-51). */
  fotoSrc?: string;
  /** Injetável para teste; por padrão desenha no canvas. */
  gerar?: typeof gerarCartaoPng;
}

export default function CartaoCompartilhar({ dados, avatar, fotoSrc, gerar = gerarCartaoPng }: CartaoCompartilharProps) {
  const [baixando, setBaixando] = useState(false);

  async function baixar() {
    if (baixando) return;
    setBaixando(true);

    try {
      // Mesma regra do UserAvatar do cabeçalho: sem tipo de imagem e com avatar configurado.
      const avatarMontado = !fotoSrc && !avatar.avatar_type && avatar.avatar ? (avatar.avatar as object) : undefined;
      baixarArquivo(await gerar(dados, logoJogaJunto, fotoSrc, avatarMontado), nomeDoArquivoDoCartao(dados.ciclo));
    } catch (e) {
      // Canvas indisponível neste navegador: não há o que baixar.
    } finally {
      setBaixando(false);
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className={TITULO_DE_SECAO}>Sua participação no ciclo</h2>
        <p className="m-0 text-[13px] text-content">Cartão para compartilhar</p>
      </div>
      <div className="flex flex-wrap items-center gap-7 rounded-2xl bg-surface-0 px-5 py-6 shadow-jj-01 lg:px-[26px]">
        <div
          aria-hidden="true"
          className="relative flex w-[308px] max-w-full shrink-0 -rotate-[1.8deg] flex-col items-center gap-[18px] overflow-hidden rounded-[20px] bg-brand-blue px-6 pb-6 pt-[22px] text-center text-brand-white shadow-[0_12px_26px_rgba(0,45,75,0.24)] transition hover:-translate-y-1 hover:rotate-0 hover:shadow-[0_18px_34px_rgba(0,45,75,0.3)]"
        >
          <CamadaDecorativa icones={DECORACAO} opacidade={0.18} emBranco />
          <div className="relative flex items-center justify-between gap-3 self-stretch">
            <img src={logoJogaJunto} alt="" className="block w-24" />
            <span className="whitespace-nowrap rounded-jj-pill bg-white/[0.16] px-[13px] py-1.5 font-display text-xs font-bold">
              {dados.pontos}
            </span>
          </div>
          <div className="relative flex flex-col items-center gap-3">
            <div className="h-24 w-24 rounded-full shadow-[0_0_0_4px_var(--brand-yellow),0_8px_18px_rgba(0,45,75,0.3)]">
              <UserAvatar data={{ ...avatar, ariaHidden: true }} responsive />
            </div>
            <div>
              <div className="font-display text-[21px] font-extrabold leading-[1.1]">{dados.nome}</div>
              <div className="mt-[5px] text-[11.5px] leading-[1.4] tracking-[0.02em] text-white/[0.88]">{dados.linhaDeApoio}</div>
            </div>
          </div>
          {dados.nivel && (
            <div className="relative flex flex-col items-center gap-3.5 self-stretch">
              <span className="h-px self-stretch bg-white/[0.35]" />
              <span className="rounded-jj-pill bg-brand-yellow px-[18px] py-2 font-display text-[13px] font-bold text-brand-blue">
                Nível {dados.nivel}
              </span>
            </div>
          )}
        </div>

        <div className="flex min-w-[250px] flex-1 flex-col gap-3.5">
          <p className="m-0 font-display text-lg font-bold text-brand-blue-dark">Mostre para a sua turma</p>
          <p className="m-0 max-w-[52ch] text-sm text-content-soft">
            Baixe o cartão do seu ciclo e compartilhe no Teams, no mural da sua unidade ou nas redes. Quem #JogaJunto merece
            aparecer.
          </p>
          <div>
            <Button variant="principal" onClick={baixar} disabled={baixando}>
              {baixando ? 'Gerando imagem…' : 'Baixar imagem'}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
